import type { QuestionFigure } from '@padcanvas/shared';
import type { ReactNode } from 'react';
import { buildQuestionDisplayText } from '@padcanvas/shared';
import { LatexText, QuestionFigures } from '../latex';
import { LoadingSpinner, IdControl, ImageViewer } from '../components';
import './QuestionPreview.css';

export interface QuestionPreviewData {
  id: string;
  subject?: string;
  difficulty?: string;
  questionType?: string | null;
  module?: { id: string; name: string } | null;
  questionText: string;
  parts?: Array<{ partId: string; statement?: string }> | null;
  questionDiagramUrl?: string | null;
  figures?: QuestionFigure[] | null;
  correctAnswer?: string;
  options?: { label: string; text: string; imageUrl?: string }[] | null;
  solutionImageUrl?: string | null;
}

interface QuestionPreviewProps {
  question?: QuestionPreviewData | null;
  loading?: boolean;
  onClose: () => void;
  /** Rendered at the bottom of the modal (e.g. "View Details" link) */
  renderFooter?: (question: QuestionPreviewData) => ReactNode;
}

export function QuestionPreview({ question, loading, onClose, renderFooter }: QuestionPreviewProps) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content qp-preview" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Question Preview</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {loading || !question ? (
            <div className="qp-loading"><LoadingSpinner /></div>
          ) : (
            <>
              <div className="preview-meta">
                {question.subject && <span className="subject-badge">{question.subject}</span>}
                {question.difficulty && <span className="preview-badge">{question.difficulty}</span>}
                {question.questionType && <span className="preview-badge">{question.questionType}</span>}
                {question.module?.name && <span className="preview-badge-muted">{question.module.name}</span>}
                <IdControl value={question.id} container="questions" />
              </div>

              <div className="preview-text"><LatexText text={buildQuestionDisplayText(question)} figures={question.figures} /></div>

              {question.questionDiagramUrl && (
                <ImageViewer src={question.questionDiagramUrl} alt="Question diagram" className="preview-image" />
              )}
              <QuestionFigures text={question.questionText} figures={question.figures} className="preview-image" />

              {question.options && question.options.length > 0 && (
                <div className="preview-options">
                  {question.options.map((opt, idx) => (
                    <div key={idx} className="preview-option">
                      <span className="option-label">{opt.label.toUpperCase()}.</span>
                      <span><LatexText text={opt.text} /></span>
                      {opt.imageUrl && <ImageViewer src={opt.imageUrl} alt={opt.label} className="option-image" />}
                    </div>
                  ))}
                </div>
              )}

              {question.correctAnswer && (
                <div className="preview-answer">
                  <strong>Correct Answer:</strong> <LatexText text={question.correctAnswer} />
                </div>
              )}

              {question.solutionImageUrl && (
                <div className="preview-solution">
                  <strong>Solution:</strong>
                  <ImageViewer src={question.solutionImageUrl} alt="Solution" className="preview-image" />
                </div>
              )}

              {renderFooter && (
                <div className="qp-footer">
                  {renderFooter(question)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
